import { Link } from "react-router-dom";
import type { ProductDto } from "@gaole/shared";
import { rm } from "../../lib/format";
import { ProductHeroImage, ProductImage, heroCaptionTextClass } from "../ProductCard";
import { ListingTitle } from "../ListingTitle";
import { Countdown } from "../Countdown";
import { Badge } from "../ui";

interface RaffleCardProps {
  raffle: {
    id: string;
    title: string;
    status: string;
    entryClosesAt?: string;
    entryOpensAt?: string;
    ticketCount: number;
    maxTickets?: number | null;
    priceForWinner?: number;
    product: ProductDto;
  };
  variant?: "banner" | "compact" | "hero";
}

export function RaffleCard({ raffle, variant = "banner" }: RaffleCardProps) {
  const max = raffle.maxTickets ?? null;
  const pct = max && max > 0 ? Math.min(100, (raffle.ticketCount / max) * 100) : null;

  if (variant === "compact") {
    return (
      <Link
        to={`/raffles/${raffle.id}`}
        className="flex w-full items-center gap-3 rounded-2xl border border-white/5 bg-surface p-3 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5 lg:hover:border-brand-pink/30"
      >
        <ProductImage src={raffle.product.images[0] ?? null} alt="" className="h-14 w-14 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold" title={raffle.title}>{raffle.title}</p>
          <p className="mt-0.5 text-[10px] text-white/50">{raffle.ticketCount} tickets in</p>
          {raffle.entryClosesAt && (
            <div className="mt-1">
              <Countdown target={raffle.entryClosesAt} compact neon />
            </div>
          )}
        </div>
        <Badge color="green">Open</Badge>
      </Link>
    );
  }

  return (
    <Link
      to={`/raffles/${raffle.id}`}
      className="block w-full min-w-0 max-w-full overflow-hidden rounded-3xl border border-white/5 bg-surface transition-transform active:scale-[0.99] lg:hover:-translate-y-1 lg:hover:glow-brand"
    >
      <div className="relative w-full min-w-0 max-w-full">
        <ProductHeroImage
          src={raffle.product.images[0] ?? null}
          aspectClassName="aspect-[2/1] lg:aspect-[21/9]"
          overlay="full"
        />
        <div className="absolute left-3 top-3 z-30">
          <Badge color="green">{raffle.status}</Badge>
        </div>
        <div className={`absolute bottom-3 left-3 right-3 z-30 ${heroCaptionTextClass}`}>
          <ListingTitle className="font-display text-sm font-bold text-white lg:text-lg">
            {raffle.title}
          </ListingTitle>
          {raffle.priceForWinner != null && (
            <p className="text-[11px] text-white/85">Winner pays {rm(raffle.priceForWinner)}</p>
          )}
        </div>
      </div>
      <div className="space-y-2 p-3">
        {pct != null && (
          <div>
            <div className="mb-1 flex justify-between text-[10px] text-white/50">
              <span>{raffle.ticketCount} / {max} spots</span>
              <span>{Math.round(pct)}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-gradient-brand transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-white/50">{raffle.ticketCount} tickets in</span>
          {raffle.entryClosesAt && raffle.status === "OPEN" ? (
            <Countdown target={raffle.entryClosesAt} compact neon />
          ) : null}
        </div>
      </div>
    </Link>
  );
}

export function WinnerSpotlight({
  raffle,
}: {
  raffle: {
    id: string;
    title: string;
    winnerName: string | null;
    product: ProductDto;
    priceForWinner?: number;
  };
}) {
  if (!raffle.winnerName) return null;
  return (
    <Link
      to={`/raffles/${raffle.id}`}
      className="glass-panel neon-ring block rounded-2xl p-4 transition-transform lg:hover:-translate-y-0.5"
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-pink">Latest winner</p>
      <div className="mt-3 flex items-center gap-3">
        <ProductImage src={raffle.product.images[0] ?? null} alt="" className="h-16 w-16 rounded-xl" />
        <div>
          <p className="font-display text-sm font-bold">{raffle.winnerName}</p>
          <ListingTitle className="text-white/60">{raffle.title}</ListingTitle>
          {raffle.priceForWinner != null && (
            <p className="mt-1 text-xs font-semibold text-brand-pink">Won at {rm(raffle.priceForWinner)}</p>
          )}
        </div>
      </div>
    </Link>
  );
}
